import TomSelect from 'tom-select/base';
import removeButton from 'tom-select/plugins/remove_button/plugin.js';

// The bare 'tom-select' entry bundles all 14 plugins; we use one. Register just
// that one on the base build and the other 13 stay out of the estimate bundle.
TomSelect.define('remove_button', removeButton);

/**
 * The estimate form's language pickers, and the two bits of wiring that used to
 * sit inline next to them.
 *
 * Replaces select2, which was the only reason this page pulled jQuery, select2
 * and a locale file off a CDN. Tom Select does the same job (type to search,
 * multiple values, keyboard, ARIA) without jQuery and from our own bundle.
 *
 * No stylesheet import here on purpose: this entry is loaded next to the app
 * entry, and importing web.scss again would emit the whole stylesheet twice.
 * The Tom Select theme rides along in web.scss instead.
 */

const LANGUAGE_FIELDS = ['#form-input-sourceLanguage', '#form-input-targetLanguages', '#form-input-targetLanguage'];

/**
 * select2 shipped a locale file for the "no results" string; Tom Select does
 * not. Rather than carry a translation map here, the form emits the already
 * translated string as a data attribute (see estimate-form.twig), the same way
 * dropzone.tsx reads its strings. Keeps it in the translation pipeline the CI
 * check covers.
 */
const noResultsText = (): string =>
    document.querySelector<HTMLElement>('[data-freeform-variant="estimate"]')?.dataset.noResults ?? '';

/**
 * Freeform renders the placeholder as the first, empty option. Tom Select shows
 * it as the field's placeholder instead, which keeps it out of the result list.
 */
const placeholderOf = (select: HTMLSelectElement): string | undefined => {
    const first = select.options[0];
    return first && first.value === '' ? first.text : undefined;
};

const initLanguageSelects = (): TomSelect[] =>
    LANGUAGE_FIELDS.flatMap((selector) => {
        const select = document.querySelector<HTMLSelectElement>(selector);
        if (!select) {
            return [];
        }

        const tomSelect = new TomSelect(select, {
            placeholder: placeholderOf(select),
            // Freeform decides the order, and for languages that order is
            // meaningful, so leave it alone.
            sortField: undefined,
            // Every option is already in the DOM, so searching stays local.
            maxOptions: null,
            // No 300ms wait before filtering: that throttle exists to batch
            // remote loads, and there is no remote load here. This is what
            // made the search feel slower than select2.
            refreshThrottle: 0,
            plugins: select.multiple ? ['remove_button'] : [],
            // A single language is a required answer: offering a way back to
            // "nothing chosen" only invites an invalid submit.
            allowEmptyOption: false,
            // Close the list after a pick, so a click behaves like a normal
            // select rather than leaving the menu hanging open.
            closeAfterSelect: true,
            // Tom Select keeps the typed letters after a pick; select2
            // cleared them. Clear them so the next search starts fresh.
            onItemAdd: function () {
                this.setTextboxValue('');
            },
            render: {
                no_results: () => `<div class="no-results">${noResultsText()}</div>`,
            },
        });

        // With the list closed, Enter has no option to pick, so swallow it
        // before Tom Select sees it: capture runs ahead of its own keydown, which
        // is bound without capture, and that stops it selecting an option the
        // user cannot see. (The form-submit half is handled once, below.)
        tomSelect.control_input.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'Enter' && !tomSelect.isOpen) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }
            },
            true
        );

        return [tomSelect];
    });

/**
 * The calculator links here with the visitor's answers in the query string, so
 * they do not have to pick them a second time.
 */
const applyCalculatorAnswers = (selects: TomSelect[]): void => {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('sl');
    const target = params.get('tl');
    const quality = params.get('ql');

    const setValue = (selector: string, value: string): void => {
        selects.find((instance) => instance.input.matches(selector))?.setValue(value, false);
    };

    if (source) {
        setValue('#form-input-sourceLanguage', source);
    }

    if (target) {
        // Either variant of the form may be on the page, never both.
        setValue('#form-input-targetLanguages', target);
        setValue('#form-input-targetLanguage', target);
    }

    if (quality) {
        for (const input of document.querySelectorAll<HTMLInputElement>('.form-group-qualityLevel .form-check input')) {
            if (input.value.toLowerCase() === quality.toLowerCase()) {
                input.checked = true;
                syncPostEditing(input);
            }
        }
    }
};

/**
 * Post-editing is a quality level to the visitor but a separate answer to the
 * back end, so the hidden field follows the radio.
 */
const syncPostEditing = (input: HTMLInputElement): void => {
    const postEditing = document.querySelector<HTMLInputElement>('#form-input-postEditing');
    if (postEditing) {
        postEditing.value = input.value === 'postEditing' ? 'yes' : 'no';
    }
};

/**
 * Enter inside a language picker must not send the form. Tom Select's search box
 * is a real text input inside the form, so the browser's implicit submit fires
 * its default button, and preventing the key events does not stop that. Blocking
 * the submit itself does: when the visitor presses Enter, focus is still in the
 * picker, whereas a real click moves focus to the button.
 */
const blockImplicitSubmit = (selects: TomSelect[]): void => {
    const form = selects[0]?.input.closest('form');
    // Capture, so this runs before Freeform's own submit handler: stopping only
    // the default is not enough, because Freeform would still send its AJAX
    // request. stopImmediatePropagation keeps the event from reaching it at all.
    form?.addEventListener(
        'submit',
        (event) => {
            if (document.activeElement?.closest('.ts-wrapper')) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        },
        true
    );
};

const init = (): void => {
    const selects = initLanguageSelects();

    // Only the submit guard needs a language field to hang off. The quality-level
    // sync and the calculator prefill stood on their own in the old script, and
    // should keep doing so: applyCalculatorAnswers already skips a language it
    // cannot find, so nothing here breaks if the form is ever built without one.
    if (selects.length > 0) {
        blockImplicitSubmit(selects);
    }

    for (const input of document.querySelectorAll<HTMLInputElement>('.form-group-qualityLevel .form-check input')) {
        input.addEventListener('change', () => syncPostEditing(input));
    }

    applyCalculatorAnswers(selects);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
